// ============================================================
//  src/pages/billing/BillingCallbackPage.js
//
//  PocketFi redirects here after payment.
//  We read the payment_id from localStorage (saved before redirect)
//  instead of from the URL — PocketFi doesn't append query params.
//
//  In your upgrade handler, before redirecting to PocketFi, call:
//    localStorage.setItem('pocketfi_pending_payment_id', payment_id);
//  This page reads it back, confirms, then clears it on success.
// ============================================================
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { confirmPayment } from '../../api/client';

const STORAGE_KEY = 'pocketfi_pending_payment_id';

export default function BillingCallbackPage() {
  const navigate = useNavigate();

  // 'verifying' | 'success' | 'pending' | 'failed' | 'error' | 'missing'
  const [status,   setStatus]   = useState('verifying');
  const [message,  setMessage]  = useState('');
  const [planName, setPlanName] = useState('');
  const [paymentId, setPaymentId] = useState('');

  useEffect(() => {
    const storedId = localStorage.getItem(STORAGE_KEY);

    if (!storedId) {
      setStatus('missing');
      return;
    }

    setPaymentId(storedId);

    // Retry up to MAX_ATTEMPTS times with a delay between each.
    // PocketFi's confirm endpoint often returns 'pending' for a few
    // seconds right after redirect even when the payment actually went
    // through — the webhook fires slightly later and is the real source
    // of truth, but retrying here catches the fast-confirm case cleanly.
    const MAX_ATTEMPTS = 5;
    const RETRY_DELAY  = 3000; // ms between retries
    let attempt = 0;

    async function tryConfirm() {
      attempt++;
      try {
        const res = await confirmPayment(storedId);
        const s   = res.data?.status;
        setPlanName(res.data?.plan || '');
        setMessage(res.data?.message || '');

        if (s === 'success') {
          localStorage.removeItem(STORAGE_KEY);
          setStatus('success');
        } else if (s === 'pending' && attempt < MAX_ATTEMPTS) {
          // Still pending — wait and retry
          setTimeout(tryConfirm, RETRY_DELAY);
        } else if (s === 'pending') {
          // Exhausted retries — show pending UI, webhook will activate the plan
          setStatus('pending');
        } else {
          setStatus('failed');
        }
      } catch {
        if (attempt < MAX_ATTEMPTS) {
          setTimeout(tryConfirm, RETRY_DELAY);
        } else {
          setStatus('error');
        }
      }
    }

    tryConfirm();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const screens = {
    verifying: {
      icon:    <span className="spinner spinner-dark" style={{ width: 40, height: 40, borderWidth: 4 }} />,
      title:   'Verifying Payment…',
      desc:    'Please wait while we confirm your payment with PocketFi. This may take a few seconds…',
      color:   'var(--text-muted)',
      actions: null,
    },
    success: {
      icon:    <div style={iconCircle('#e6f7f2', '#00c88c')}>✓</div>,
      title:   'Payment Successful!',
      desc:    planName
        ? `Your ${planName} plan is now active. Enjoy your new features.`
        : 'Your plan has been activated. Enjoy your new features.',
      color:   'var(--success)',
      actions: (
        <>
          <button className="btn btn-primary" onClick={() => navigate('/dashboard')}>
            Go to Dashboard
          </button>
          <button className="btn btn-outline" onClick={() => navigate('/billing')}>
            View Billing
          </button>
        </>
      ),
    },
    pending: {
      icon:    <div style={iconCircle('#fff8e6', '#f5a623')}>⏳</div>,
      title:   'Payment Pending',
      desc:    'Your payment is still being processed. Your plan will activate automatically once confirmed — usually within a few minutes.',
      color:   '#f5a623',
      actions: (
        <>
          <button className="btn btn-primary" onClick={() => navigate('/dashboard')}>
            Go to Dashboard
          </button>
          <button className="btn btn-outline" onClick={() => navigate('/billing')}>
            Check Billing
          </button>
        </>
      ),
    },
    failed: {
      icon:    <div style={iconCircle('#fef2f2', '#e53e3e')}>✕</div>,
      title:   'Payment Failed',
      desc:    message || 'Your payment was not successful. Please try again or contact support.',
      color:   'var(--danger)',
      actions: (
        <>
          <button className="btn btn-primary" onClick={() => navigate('/billing')}>
            Try Again
          </button>
          <button className="btn btn-outline" onClick={() => navigate('/dashboard')}>
            Go to Dashboard
          </button>
        </>
      ),
    },
    error: {
      icon:    <div style={iconCircle('#fef2f2', '#e53e3e')}>!</div>,
      title:   'Verification Error',
      desc:    'We could not verify your payment. If you were charged, please contact support with your payment reference.',
      color:   'var(--danger)',
      actions: (
        <>
          <button className="btn btn-primary" onClick={() => navigate('/billing')}>
            Go to Billing
          </button>
          <button className="btn btn-outline" onClick={() => navigate('/dashboard')}>
            Go to Dashboard
          </button>
        </>
      ),
    },
    missing: {
      icon:    <div style={iconCircle('#f0f0f0', '#888')}>?</div>,
      title:   'No Payment Reference',
      desc:    'We couldn\'t find a pending payment in this browser session. If you just completed a payment, your plan may already be active — check your billing page.',
      color:   'var(--text-muted)',
      actions: (
        <button className="btn btn-primary" onClick={() => navigate('/billing')}>
          Go to Billing
        </button>
      ),
    },
  };

  const screen = screens[status];

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 24,
      background: 'var(--page-bg, #f8fafc)',
    }}>
      <div style={{
        background: 'var(--white, #fff)',
        border: '1.5px solid var(--border, #e2e8f0)',
        borderRadius: 'var(--radius-lg, 16px)',
        padding: '40px 32px',
        maxWidth: 440,
        width: '100%',
        textAlign: 'center',
        boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
      }}>

        {/* Icon */}
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
          {screen.icon}
        </div>

        {/* Title */}
        <div style={{
          fontSize: 20,
          fontWeight: 800,
          color: 'var(--text-dark)',
          marginBottom: 10,
        }}>
          {screen.title}
        </div>

        {/* Description */}
        <div style={{
          fontSize: 13.5,
          color: 'var(--text-muted)',
          lineHeight: 1.6,
          marginBottom: 28,
        }}>
          {screen.desc}
        </div>

        {/* Actions */}
        {screen.actions && (
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
            {screen.actions}
          </div>
        )}

        {/* Payment ID footnote — now sourced from state, not URL */}
        {paymentId && status !== 'verifying' && (
          <div style={{
            marginTop: 24,
            fontSize: 11,
            color: 'var(--text-muted)',
            fontFamily: 'monospace',
          }}>
            Ref: {paymentId}
          </div>
        )}
      </div>
    </div>
  );
}

function iconCircle(bg, color) {
  return {
    width: 64,
    height: 64,
    borderRadius: '50%',
    background: bg,
    color: color,
    fontSize: 28,
    fontWeight: 800,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };
}